<template>
  <li class="flex items-center pl-4 pr-1 hover:bg-gray-200 group">
    <!-- Status -->
    <fa
      v-if="material.status === 'READY' && hasFile"
      :icon="faCheck"
      fixed-width
      class="text-green-500"
      :title="$t('components.landingpages.fileReady')"
    />
    <fa
      v-if="material.status === 'READY' && !hasFile && pendingVersion"
      :icon="faClock"
      fixed-width
      class="text-blue-500"
      :title="$t('components.landingpages.versionNeedsReview', {
        version: pendingVersion.versionNumber,
        errors: pendingVersionQuality.errorCount,
        warnings: pendingVersionQuality.warningCount
      })"
    />
    <fa
      v-if="material.status === 'PENDING'"
      :icon="faClock"
      fixed-width
      class="text-gray-400"
      :title="$t('components.landingpages.generationPending')"
    />
    <fa
      v-if="material.status === 'QUEUED'"
      :icon="faSpinner"
      fixed-width
      class="animate-spin"
      :title="$t('components.landingpages.queued')"
    />
    <fa
      v-if="material.status === 'GENERATING'"
      :icon="faSpinner"
      fixed-width
      class="animate-spin"
      :title="$t('components.landingpages.generating')"
    />
    <fa
      v-if="material.status === 'GENERATION_FAILED'"
      :icon="faExclamationTriangle"
      fixed-width
      class="text-yellow-500"
      :title="$t('components.landingpages.generationFailed')"
    />

    <!-- File name -->
    <div class="flex-1 px-4 py-2">
      {{ material.displayName }}
    </div>

    <!-- Mode (manual) -->
    <div class="flex lg:w-32">
      <template v-if="material.type === 'DOWNLOAD'">
        <span
          class="text-sm text-gray-600"
          :class="material.mode !== 'MANUAL' && 'invisible group-hover:visible'"
        >
          {{ $t('components.landingpages.materialModeManual') }}
        </span>
        <InputSwitch
          class="invisible ml-2 group-hover:visible"
          :value="material.mode === 'MANUAL'"
          small
          @onChange="switchMode"
        />
      </template>
    </div>

    <!-- Version -->
    <div class="w-6 pl-2">
      <template v-if="hasFile">
        v{{ material.version }}
      </template>
    </div>

    <!-- Review -->
    <!-- Always rendered so a pending version does not push the following
         fixed-width columns out of line with every other row. -->
    <div class="px-2 text-xs lg:w-48 text-yellow-700">
      <template v-if="pendingVersion">
        {{ $t('components.landingpages.versionNeedsReview', {
          version: pendingVersion.versionNumber,
          errors: pendingVersionQuality.errorCount,
          warnings: pendingVersionQuality.warningCount
        }) }}
        <button
          class="ml-1 btn-inline-small"
          @click="approvePendingVersion"
        >
          {{ $t('components.landingpages.approveVersion') }}
        </button>
        <a
          v-if="pendingVersion.file"
          class="ml-1 btn-inline-small"
          :href="createAttachmentUrl(pendingVersion.file)"
          target="_blank"
        >
          {{ $t('components.landingpages.reviewVersion') }}
        </a>
      </template>
    </div>

    <!-- Update Date -->
    <div class="pl-2 lg:w-40">
      <template v-if="hasFile">
        <span
          class="text-sm text-gray-600"
          :title="formatDateTime(material.updatedAt)"
        >
          {{ formatDate(material.updatedAt) }}
        </span>
      </template>
    </div>

    <!-- Actions -->
    <div class="flex invisible pl-4 space-x-1 group-hover:visible">
      <!-- Action: preview -->
      <div
        v-if="hasFile"
        class="relative flex w-8 group"
      >
        <div class="absolute right-0 z-10 flex flex-col items-center p-1 mb-6 -mt-1 bg-gray-200 rounded-md">
          <a
            v-for="{file} in material.files"
            :key="file.cdnUrl"
            :href="createAttachmentUrl(file)"
            target="_blank"
            :title="$t('components.landingpages.preview')"
            class="btn-icon"
          >
            <fa
              :icon="faEye"
              fixed-width
            />
          </a>
        </div>
      </div>

      <!-- Action: preview disabled -->
      <button
        v-else
        class="btn-icon"
        disabled
      >
        <fa
          :icon="faEye"
          fixed-width
        />
      </button>
      <button
        v-if="canUnpublish"
        :title="$t('components.landingpages.unpublishVersion')"
        class="btn-icon"
        @click="unpublishVersion"
      >
        <fa
          :icon="faEyeSlash"
          fixed-width
        />
      </button>

      <material-trace-history
        v-if="hasRole(ROLES.SUPER_ADMIN)"
        :material-id="material.id"
        :company-id="companyId"
      />

      <!-- Action: generate -->
      <button
        v-if="hasRole(ROLES.SUPER_ADMIN) && material.mode === 'GENERATED'"
        :title="$t('components.landingpages.generate')"
        class="btn-icon"
        @click="showGenerateModal()"
      >
        <fa
          :icon="faSync"
          fixed-width
        />
      </button>

      <!-- Action: upload -->
      <button
        v-if="material.mode === 'MANUAL'"
        :title="$t('components.landingpages.uploadNewVersion')"
        class="btn-icon"
        @click="showUploadModal()"
      >
        <fa
          :icon="faUpload"
          fixed-width
        />
      </button>

      <!-- Fallback placeholder if no action -->
      <div
        v-if="!['GENERATED', 'MANUAL'].includes(material.mode)"
        class="w-8"
      />
    </div>
  </li>
</template>

<script>
import {
  faCheck,
  faSpinner,
  faClock,
  faExclamationTriangle,
  faUpload,
  faSync,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons'
import UploadModal from '@/components/modals/landingpage/upload'
import GenerateOneModal from '@/components/modals/landingpage/generate-one'
import InputSwitch from '@/components/form/switch.vue'
import UPDATE_MATERIAL_MODE from '@/graphql/material/updateMaterialMode.gql'
import role from '@/mixins/role'
import eventBus, { eventNames } from '@/lib/eventBus'
import MATERIALS from '@/graphql/material/materials.gql'
import APPROVE_MATERIAL_VERSION from '@/graphql/ApproveMaterialVersion.gql'
import UNPUBLISH_MATERIAL_VERSION from '@/graphql/UnpublishMaterialVersion.gql'
import materialTraceHistory from './material-trace-history'

const SOCIAL_ASSET_TYPES = ['SOCIAL_IMAGE', 'SOCIAL_STORY']

export default {
  components: {
    InputSwitch,
    materialTraceHistory
  },

  mixins: [role],

  props: {
    material: {
      type: Object,
      required: true
    },
    companyId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      faCheck,
      faSpinner,
      faClock,
      faExclamationTriangle,
      faUpload,
      faSync,
      faEye,
      faEyeSlash
    }
  },

  computed: {
    // A social image is one variant of a five-image set. The set publishes and
    // withdraws as a whole, so neither action is offered per variant here.
    isSocialAsset () {
      return SOCIAL_ASSET_TYPES.includes(this.material.assetType)
    },
    pendingVersion () {
      if (this.isSocialAsset) return undefined
      return this.material.versions?.find(version => version.state === 'PENDING_REVIEW')
    },
    pendingVersionQuality () {
      return this.pendingVersion?.quality || { errorCount: 0, warningCount: 0 }
    },
    canUnpublish () {
      return Boolean(this.material.publishedVersion) && !this.isSocialAsset
    },
    // material.hasFile is not being updated when changing mode
    // so we make our own check.
    hasFile () {
      return this.material.files.length > 0
    }
  },

  methods: {
    /** Refresh the list, then let the screen resume watching for new work. */
    afterMaterialChange () {
      return [{ query: MATERIALS, variables: { companyId: this.companyId } }]
    },

    announceWorkStarted () {
      eventBus.$emit(eventNames.MATERIAL_WORK_STARTED, { companyId: this.companyId })
    },

    async approvePendingVersion () {
      try {
        await this.$apollo.mutate({
          mutation: APPROVE_MATERIAL_VERSION,
          variables: {
            materialId: this.material.id,
            versionNumber: this.pendingVersion.versionNumber,
            companyId: this.companyId
          },
          refetchQueries: this.afterMaterialChange()
        })
        this.announceWorkStarted()
      } catch (err) {
        alert(this.$t('components.landingpages.approveVersionFailed', { error: err.message }))
      }
    },

    // Withdrawal is non-destructive: the approved version, its quality report,
    // and its stored file are all retained, so the same version can be
    // published again. The confirmation stays because the material does leave
    // the customer workspace until someone publishes it back.
    async unpublishVersion () {
      const hasConfirmed = confirm(this.$t('components.landingpages.unpublishVersionConfirm', {
        name: this.material.displayName
      }))
      if (!hasConfirmed) return

      try {
        await this.$apollo.mutate({
          mutation: UNPUBLISH_MATERIAL_VERSION,
          variables: { materialId: this.material.id, companyId: this.companyId },
          refetchQueries: this.afterMaterialChange()
        })
        this.announceWorkStarted()
      } catch (err) {
        alert(this.$t('components.landingpages.unpublishVersionFailed', { error: err.message }))
      }
    },

    showGenerateModal () {
      this.$modal.show(GenerateOneModal, {
        material: this.material
      })
    },

    showUploadModal () {
      this.$modal.show(UploadModal, {
        material: this.material
      })
    },

    createAttachmentUrl ({ cdnUrl }) {
      const newUrl = new URL(cdnUrl)
      if (!cdnUrl.includes('.mp4')) {
        newUrl.searchParams.append('attachment', 'false')
      }
      return newUrl.toString()
    },

    switchMode () {
      try {
        this.$apollo.mutate({
          mutation: UPDATE_MATERIAL_MODE,
          variables: {
            input: {
              materialId: this.material.id,
              mode: this.material.mode === 'MANUAL' ? 'GENERATED' : 'MANUAL'
            }
          }
        })
      } catch (err) {
        alert(this.$t('components.landingpages.switchModeFailed'))
      }
    }
  }
}
</script>
